import styles from './FeeCalculatorSection.module.css';
import { useTypedTranslation } from '@/hooks/useTypedTranslation';
import { Calculator } from './Calculator';
import { useState } from 'react';
import ItalianFlag from '@/components/ui/icons/flags/ItalianFlag';
import SpanishFlag from '@/components/ui/icons/flags/SpanishFlag';
import ChevronDownIcon from '@/components/ui/icons/ChevronDownIcon';
import ChevronTopIcon from '@/components/ui/icons/ChevronTopIcon';

const flagData = [
  { code: 'it', label: 'feeCalculatorSection.flagItaly', icon: '/images/flag-it.png' },
  { code: 'es', label: 'feeCalculatorSection.flagSpain', icon: '/images/flag-es.png' },
];

export default function FeeCalculatorSection() {
  const [selectedFlag, setSelectedFlag] = useState<'it' | 'es'>('it');
  const [showHighSpendingSection, setShowHighSpendingSection] = useState(false);
  const [highSpendingAmount, setHighSpendingAmount] = useState<number | null>(null);
  const { t } = useTypedTranslation();
  // Marker/slider visual logic via hook
  const markerLabelsItaly = ['70', '150', '300', '500', '2k', '5k', '10k', '20k'];
  const markerLabelsSpain = ['0', '20', '150', '300', '500', '2k', '5k', '10k', '20k'];
  const minItaly = 0;
  const maxItaly = 10000;
  const minSpain = 0;
  const maxSpain = 10000;

  return (
    <section className={styles.feeCalculatorSection}>
      <div className={styles.container}>
        <div className={styles.topHeaderContainer}>
          <div className={styles.topHeader}>{t('feeCalculatorSection.topHeader')}</div>
          <div className={styles.flagSwitcher}>
            {flagData.map((flag) => (
              <button
                key={flag.code}
                className={
                  selectedFlag === flag.code
                    ? `${styles.flagButton} ${styles.activeFlag}`
                    : styles.flagButton
                }
                aria-label={t(flag.label as `feeCalculatorSection.${string}`)}
                type="button"
                onClick={() => setSelectedFlag(flag.code as 'it' | 'es')}
              >
                <span className={styles.flagIcon}>
                  {flag.code === 'it' ? <ItalianFlag /> : <SpanishFlag />}
                </span>
                {t(flag.label as `feeCalculatorSection.${string}`)}
              </button>
            ))}
          </div>
        </div>
        <div className={styles.contentContainer}>
          <div className={styles.content}>
            <div className={styles.hero}>{t('feeCalculatorSection.hero')}</div>
            <div className={styles.description}>{t('feeCalculatorSection.description')}</div>
          </div>
          <Calculator
            markerLabels={selectedFlag === 'it' ? markerLabelsItaly : markerLabelsSpain}
            min={selectedFlag === 'it' ? minItaly : minSpain}
            max={selectedFlag === 'it' ? maxItaly : maxSpain}
            locale={selectedFlag}
          >
            <button
              className={styles.ctaButton}
              onClick={() => setShowHighSpendingSection(!showHighSpendingSection)}
            >
              <span>{t('feeCalculatorSection.highSpendingButton')}</span>
              {showHighSpendingSection ? (
                <ChevronTopIcon stroke="#fff" />
              ) : (
                <ChevronDownIcon stroke="#fff" />
              )}
            </button>
            {showHighSpendingSection && (
              <div className={styles.highSpendingSection}>
                <div className={styles.highSpendingContent}>
                  <div className={styles.invoiceAmountLabel}>Invoice amount</div>
                  <div className={styles.inputContainer}>
                    <span className={styles.currencySymbol}>€</span>
                    <input
                      id="highSpendingAmount"
                      type="number"
                      value={highSpendingAmount === null ? '' : highSpendingAmount}
                      onChange={(e) =>
                        setHighSpendingAmount(e.target.value === '' ? null : Number(e.target.value))
                      }
                      className={styles.invoiceInput}
                      placeholder="20.001"
                    />
                  </div>

                  <div className={styles.highFeeBreakdown}>
                    <div className={styles.highFeeBreakdownFirstRow}>
                      <span className={styles.highFeeBreakdownLabelFirstRow}>
                        {t('feeCalculatorSection.breakdown.agencyFee')}
                      </span>
                      <span className={styles.highFeeBreakdownValueFirstRow}>
                        <span style={{ color: '#717f8e' }}>
                          {t('feeCalculatorSection.invoiceValue.currency')}
                        </span>{' '}
                        {(highSpendingAmount ? highSpendingAmount * 0.07 : 0).toLocaleString(
                          'en-US',
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }
                        )}
                      </span>
                    </div>
                    <div className={styles.highFeeBreakdownSecondRow}>
                      <span className={styles.highFeeBreakdownLabelSecondRow}>
                        {t('feeCalculatorSection.breakdown.stampFee')}
                      </span>
                      <div className={styles.highFeeBreakdownValueSecondRowContainer}>
                        <span className={styles.highFeeBreakdownValueSecondRow}>
                          {t('feeCalculatorSection.invoiceValue.currency')}{' '}
                          {(highSpendingAmount ? highSpendingAmount * 0.01 : 0).toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )}
                        </span>
                        <span className={styles.percentageText}> (1% of your invoice)</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Calculator>
        </div>
      </div>
    </section>
  );
}
